import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, History, LoaderCircle, X } from 'lucide-react';
import { ExportHistoryFilters } from './ExportHistoryFilters';
import { ExportHistoryRow } from './ExportHistoryRow';
import { openDocument } from './open-document';
import type { PaigeExportRow } from './export-types';
import type { PaigeWorkbenchBridge } from './paige.global';
import { PAGE, useExportHistory } from './use-export-history';
import './export-history.css';

/**
 * The job's export history (parity rows E-25, E-40), over the workbench in the destination sheet's surface: every
 * delivery to the job's destinations, newest first, searched and filtered on the server and paged forty at a time,
 * with Retry delivery, Cancel delivery and Send back for review on the rows they apply to. Send back for review
 * closes the sheet and Job setup and opens the document on the job's documents.
 */
export function ExportHistorySheet(props: { bridge: PaigeWorkbenchBridge; onClose: () => void }) {
  const { bridge, onClose } = props;
  const history = useExportHistory(bridge.exports);
  const types = useJobTypes(bridge);
  useEscape(onClose);
  const body = bodyOf(history, bridge, (row) => (onClose(), openDocument(row.documentId)));
  return (
    <div className="paige-intake-backdrop" onClick={onClose}>
      <section
        className="paige-intake-sheet paige-export-history"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paige-export-history-title"
        onClick={(event) => event.stopPropagation()}
      >
        <SheetHead onClose={onClose} />
        <ExportHistoryFilters
          applied={history.applied}
          types={types}
          busy={history.rows === null && !history.error}
          onFind={history.find}
        />
        <Body {...body} />
        <Pages newer={history.newer} older={history.older} page={history.page} />
      </section>
    </div>
  );
}

function SheetHead({ onClose }: { onClose: () => void }) {
  return (
    <>
      <button type="button" className="paige-intake-close" onClick={onClose} aria-label="Close">
        <X size={16} />
      </button>
      <header className="paige-export-history-head">
        <History size={22} aria-hidden="true" />
        <span>
          <h2 id="paige-export-history-title">Export history</h2>
          <small>Every document sent to this job’s destinations, newest first.</small>
        </span>
      </header>
    </>
  );
}

function Pages({
  newer,
  older,
  page,
}: {
  newer: (() => void) | null;
  older: (() => void) | null;
  page: number;
}) {
  if (!newer && !older) return null;
  return (
    <nav className="paige-export-pages" aria-label="Pages">
      <button
        type="button"
        className="quiet-button small"
        disabled={!newer}
        onClick={() => newer?.()}
      >
        <ChevronLeft size={12} aria-hidden="true" /> Newer
      </button>
      <small>Page {page}</small>
      <button
        type="button"
        className="quiet-button small"
        disabled={!older}
        onClick={() => older?.()}
      >
        Older <ChevronRight size={12} aria-hidden="true" />
      </button>
    </nav>
  );
}

interface BodyProps {
  rows: PaigeExportRow[] | null;
  error: string | null;
  filtered: boolean;
  runsJobs: boolean;
  onAct: (action: 'retry' | 'cancel', row: PaigeExportRow) => Promise<void>;
  onReview: (row: PaigeExportRow) => void;
  onRetry: () => void;
}

/** What the page's body shows and does; only someone who runs the job, and not a read-only look, may act. */
function bodyOf(
  history: ReturnType<typeof useExportHistory>,
  bridge: PaigeWorkbenchBridge,
  onReview: (row: PaigeExportRow) => void,
): BodyProps {
  return {
    rows: history.rows,
    error: history.error,
    filtered: Object.values(history.applied).some((value) => value),
    runsJobs: bridge.runsJobs !== false && bridge.readOnly !== true,
    onAct: history.act,
    onReview,
    onRetry: history.reload,
  };
}

/** The page: the error with Try again, loading, nothing yet, nothing that matches, or the rows. */
function Body(props: BodyProps) {
  const { rows, error, filtered, runsJobs, onAct, onReview, onRetry } = props;
  if (error) return <LoadError error={error} onRetry={onRetry} />;
  if (rows === null)
    return (
      <p className="paige-export-history-empty" role="status">
        <LoaderCircle className="paige-spin" size={14} aria-hidden="true" /> Loading export history…
      </p>
    );
  if (rows.length === 0)
    return (
      <div className="paige-export-history-empty">
        <History size={28} aria-hidden="true" />
        <span>
          {filtered ? 'No exported documents match these filters.' : 'Nothing exported yet'}
        </span>
      </div>
    );
  return (
    <ul
      className="paige-export-rows"
      aria-label={`Exports, ${rows.length} of at most ${PAGE} on this page`}
    >
      {rows.map((row) => (
        <ExportHistoryRow
          key={row.id}
          row={row}
          runsJobs={runsJobs}
          onAct={onAct}
          onReview={onReview}
        />
      ))}
    </ul>
  );
}

function LoadError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="paige-export-history-error" role="alert">
      <span>
        <strong>The export history could not be loaded</strong>
        <small>{error}</small>
      </span>
      <button type="button" className="secondary-button small" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

/** The job's document types by name, for the type filter, from the job's file naming catalog. */
function useJobTypes(bridge: PaigeWorkbenchBridge): string[] {
  const [types, setTypes] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    bridge.filingPlan
      ?.read()
      .then((plan) => live && setTypes(plan.types.map((type) => type.name)))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [bridge]);
  return types;
}
